import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Copy, Mail, Share2 } from "lucide-react";
import { useToasts } from "../hooks/useToasts";
import "./ShareButton.css";

const WhatsAppIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 11.5a8 8 0 0 1-11.8 7L4 20l1.7-4A8 8 0 1 1 20 11.5Z" />
    <path d="M9.6 8.8c.2-.5.4-.5.7-.5h.6c.2 0 .4.1.5.4l.8 1.9c.1.2.1.4 0 .6l-.4.5c-.1.2-.1.4 0 .5.4.7 1 1.3 1.6 1.7.2.1.4.1.5 0l.6-.4c.2-.1.4-.1.6 0l1.8.8c.3.1.4.3.4.5v.6c0 .3-.1.5-.5.7-.5.2-1 .4-1.7.3-1-.1-2-.5-3-1.2a9 9 0 0 1-2.5-2.4c-.7-1-1.1-2-1.2-3-.1-.7.1-1.2.3-1.7Z" />
  </svg>
);

const XIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 4h4.4l4 5.3L16.8 4H20l-6.1 7.1L20 20h-4.4l-4.3-5.6L7.2 20H4l6.4-7.5Z" />
  </svg>
);

const FacebookIcon = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M13.5 20v-7h2.4l.4-3h-2.8V8.1c0-.9.3-1.6 1.6-1.6H16V4a8.6 8.6 0 0 0-1.9-.2c-2 0-3.4 1.2-3.4 3.5V10H8v3h2.7v7" />
  </svg>
);

const ShareButton = ({
  shareUrl = "/tutorial",
  shareTitle = "Asasiasa Tutorial",
  shareText = "Explore the Asasiasa tutorial.",
  className = "",
}) => {
  const { addToast } = useToasts();
  const label = "Share";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const resolvedShareUrl = useMemo(
    () =>
      typeof window !== "undefined"
        ? new URL(shareUrl, window.location.origin).toString()
        : shareUrl,
    [shareUrl]
  );

  const shareTargets = useMemo(() => {
    const encodedUrl = encodeURIComponent(resolvedShareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const encodedText = encodeURIComponent(shareText);
    const emailSubject = encodeURIComponent(shareTitle);
    const emailBody = encodeURIComponent(`${shareText}\n\n${resolvedShareUrl}`);

    return [
      {
        key: "copy",
        label: "Copy link",
        icon: Copy,
        action: "copy",
      },
      {
        key: "whatsapp",
        label: "WhatsApp",
        icon: WhatsAppIcon,
        href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      },
      {
        key: "email",
        label: "Email",
        icon: Mail,
        href: `mailto:?subject=${emailSubject}&body=${emailBody}`,
      },
      {
        key: "x",
        label: "X",
        icon: XIcon,
        href: `https://x.com/intent/post?text=${encodedTitle}&url=${encodedUrl}`,
      },
      {
        key: "facebook",
        label: "Facebook",
        icon: FacebookIcon,
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      },
    ];
  }, [resolvedShareUrl, shareText, shareTitle]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        buttonRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }

      setIsMenuOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(resolvedShareUrl);
      addToast("Tutorial link copied to clipboard", "success-info");
      return true;
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = resolvedShareUrl;
      textarea.setAttribute("readonly", "");
      textarea.className = "share-button-copy-fallback";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (copied) {
        addToast("Tutorial link copied to clipboard", "success-info");
      } else {
        addToast("Could not share the tutorial link", "error-warning");
      }

      return copied;
    }
  };

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: resolvedShareUrl,
        });
        return;
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
      }
    }

    setIsMenuOpen((currentValue) => !currentValue);
  };

  const handleCopyClick = async () => {
    await copyToClipboard();
    setIsMenuOpen(false);
  };

  const handleTargetClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <div className={`share-button-wrap ${className}`.trim()}>
      <button
        ref={buttonRef}
        type="button"
        className={`share-button${isMenuOpen ? " is-open" : ""}`}
        onClick={handleShare}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
      >
        <Share2 size={14} aria-hidden="true" />
      </button>

      {isMenuOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="share-menu-overlay" onClick={handleTargetClick}>
              <div
                ref={menuRef}
                className="share-menu"
                role="menu"
                aria-label="Share options"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="share-menu-close"
                  onClick={handleTargetClick}
                  aria-label="Close share menu"
                >
                  ×
                </button>
                <div className="share-menu-title">Share</div>
                <div className="share-menu-items">
                  {shareTargets.map((target) => {
                    const Icon = target.icon;

                    if (target.action === "copy") {
                      return (
                        <button
                          key={target.key}
                          type="button"
                          className="share-menu-item"
                          onClick={handleCopyClick}
                          role="menuitem"
                        >
                          <Icon size={14} aria-hidden="true" />
                          <span>{target.label}</span>
                        </button>
                      );
                    }

                    return (
                      <a
                        key={target.key}
                        className="share-menu-item"
                        href={target.href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={handleTargetClick}
                        role="menuitem"
                      >
                        <Icon size={14} aria-hidden="true" />
                        <span>{target.label}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default ShareButton;
